import { isEqual, noop } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import styled from 'styled-components';
import { filterToValidAttributes } from '@zocdoc/dom-property';

import { linkStyle } from '../link';

const prependWithSpace = text => ` ${text}`;

/**
 *  A component that truncates text after {maxLines} line wraps.
 *
 *  Note: This is a special component!
 *
 *  In order to truncate after X number of line wraps, we must manually edit the HTML using JavaScript. However,
 *  manually changing HTML causes React to throw an error when it tries to update the component. Therefore, all
 *  updates to this component must be handled manually.
 */
export class CollapsibleTextByLine extends Component {
    static propTypes = {
        children: PropTypes.string.isRequired,
        collapseText: PropTypes.string, // the displayed toggle text when component is expanded (e.g. "...read less")
        expandText: PropTypes.string, // the displayed toggle text when component is collapsed (e.g. "...read more")
        hasToggleText: PropTypes.bool, // whether or not to have toggle text (e.g. "...read more"/"...read less")
        maxLines: PropTypes.number, // maximum number of lines before truncating
        onClickToggle: PropTypes.func,
    };

    static defaultProps = {
        collapseText: '…read less',
        expandText: '…read more',
        hasToggleText: true,
        maxLines: 3,
        onClickToggle: noop,
    };

    // Component state
    // We can't use this.setState(), so just use private variables as a replacement for this.state.
    isExpanded = false;

    // References to DOM nodes
    toggleText = null;
    wrapperNode = null;
    textNode = null;

    rafHandle = null;

    componentDidMount() {
        this.updateComponent();

        window.addEventListener('resize', this.handleResize, true);
    }

    shouldComponentUpdate(nextProps) {
        /**
         * we don't want this component to update via React, so sCU is set to false, but we still
         * want to implement a subset of the sCU check so we're not thrashing layout for no reason
         */
        if (!isEqual(nextProps, this.props)) {
            this.updateComponent(nextProps);
        }

        return false;
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize, true);
    }

    // Note: render() only gets called once, because React never updates this component.
    render() {
        return (
            <p
                {...filterToValidAttributes(this.props)}
                ref={n => { this.wrapperNode = n; }}
                data-test="collapsible-text-by-line"
            >
                <span ref={n => { this.textNode = n; }}>
                    {this.props.children}
                </span>
                <ToggleText
                    className="hide"
                    data-test="toggle-text"
                    innerRef={n => { this.toggleText = n; }}
                    onClick={this.handleToggleExpand}
                >
                    {prependWithSpace(this.props.expandText)}
                </ToggleText>
            </p>
        );
    }

    handleResize = () => {
        if (!this.isExpanded) {
            this.updateComponent();
        }
    }

    handleToggleExpand = e => {
        e.preventDefault();
        e.stopPropagation();

        this.props.onClickToggle();

        this.isExpanded = !this.isExpanded;
        this.updateComponent();
    };

    /**
     *  Manually update the component
     */
    updateComponent = (props = this.props) => {
        if (props.className && props.className !== this.wrapperNode.className) {
            this.wrapperNode.className = props.className;
        }

        if (!props.hasToggleText) {
            this.toggleText.classList.add('hide');
        }

        this.maybeTruncateText();
    }

    /**
     *  If toggle text is enabled, set the visibility and text of the toggle text
     */
    maybeShowToggleText = (toggleTextEnabled = this.props.hasToggleText, on, text) => {
        if (!toggleTextEnabled) {
            return;
        }

        if (!on) {
            this.toggleText.classList.add('hide');
        } else {
            this.toggleText.classList.remove('hide');
        }

        if (text) {
            this.toggleText.textContent = prependWithSpace(text);
        }
    }

    /**
     *  Get the height of the text node with n={maxLines} lines
     */
    getMaxHeight = (props = this.props) => {
        const computedStyle = window.getComputedStyle(this.wrapperNode);
        const lineHeight = parseFloat(computedStyle['line-height']);
        const paddingTop = parseFloat(computedStyle['padding-top']);
        const paddingBottom = parseFloat(computedStyle['padding-bottom']);

        return Math.ceil((props.maxLines * lineHeight) + paddingTop + paddingBottom);
    }

    /**
     *  Truncate text to fit within the given height
     */
    truncateTextToFitHeight = (props = this.props, maxHeight) => {
        const words = props.children.split(' ');
        this.textNode.textContent = words[0];

        // Add words one at a time until we reach the limit
        for (let i = 1; i < words.length; ++i) {
            this.textNode.textContent += ` ${words[i]}`;
            const lastWordLength = words[i].length + 1;

            if (this.wrapperNode.clientHeight > maxHeight) {
                // Remove the last word that we added
                this.textNode.textContent = this.textNode.textContent.slice(0, -lastWordLength);
                return;
            }
        }
    }

    /**
     *  Truncate text to fit within the given number of lines
     */
    maybeTruncateText = (props = this.props) => {
        if (this.rafHandle) {
            cancelAnimationFrame(this.rafHandle);
        }

        this.rafHandle = requestAnimationFrame(() => {
            if (this.wrapperNode) {
                const maxHeight = this.getMaxHeight(props);

                // Reset the text and hide the toggle text
                this.textNode.textContent = props.children;
                this.maybeShowToggleText(props.hasToggleText, false);

                // Truncation is not needed
                if (this.wrapperNode.clientHeight <= maxHeight) {
                    return;
                }

                // Truncation is needed and text is expanded
                if (this.isExpanded) {
                    this.maybeShowToggleText(props.hasToggleText, true, props.collapseText);
                    return;
                }

                // Truncation is needed and text is collapsed
                this.maybeShowToggleText(props.hasToggleText, true, props.expandText);
                this.truncateTextToFitHeight(props, maxHeight);
            }

            this.rafHandle = null;
        });
    }
}

/**
 * using a faux border instead of the border property itself such that the border does not take up space;
 * the maxHeight calculation will get screwed up if the toggle button isn't exactly the same line height
 * as the surrounding text
 */
const ToggleText = styled.button`
    ${linkStyle};
    border: none !important;
    display: inline-block;
    font-family: inherit;
    font-size: inherit;
    margin-left: 4px;
    padding: 0;
    position: relative;

    &::before {
        border-bottom: 1px dotted currentColor;
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
    }

    &:focus::before,
    &:hover::before {
        border-bottom-style: solid;
    }

    &.hide {
        display: none;
    }
`;
